import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  Calendar,
  ArrowLeft,
  Link2,
  Unlink,
  Plus,
  Clock,
  MapPin,
  Video,
  ExternalLink,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Users
} from "lucide-react";

const CalendarIntegration = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, has_tokens: false });
  const [events, setEvents] = useState([]);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    // Check for connection status from URL params
    if (searchParams.get("calendar_connected") === "true") {
      toast.success("Google Calendar connected successfully!");
    } else if (searchParams.get("calendar_error")) {
      toast.error(`Calendar connection failed: ${searchParams.get("calendar_error")}`);
    }
    
    fetchCalendarStatus();
    fetchScheduledInterviews();
  }, [searchParams]);

  const fetchCalendarStatus = async () => {
    try {
      const response = await fetch(`${API}/calendar/status`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarStatus(data);
        if (data.connected) {
          fetchEvents();
        }
      }
    } catch (error) {
      console.error("Failed to fetch calendar status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API}/calendar/events?days=14`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error("Failed to fetch events:", error);
    }
  };

  const fetchScheduledInterviews = async () => {
    try {
      const response = await fetch(`${API}/calendar/scheduled-interviews`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledInterviews(data);
      }
    } catch (error) {
      console.error("Failed to fetch scheduled interviews:", error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API}/calendar/connect`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        toast.error("Failed to initiate calendar connection");
      }
    } catch (error) {
      toast.error("Failed to connect calendar");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const response = await fetch(`${API}/calendar/disconnect`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        setCalendarStatus({ connected: false, has_tokens: false });
        setEvents([]);
        toast.success("Calendar disconnected");
      }
    } catch (error) {
      toast.error("Failed to disconnect calendar");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/calendar/events/${eventId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        setEvents(events.filter(e => e.id !== eventId));
        toast.success("Event deleted");
      }
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
  };

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                <span className="text-crimson">CALENDAR</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">Manage your schedule and interviews</p>
            </div>
            {calendarStatus.connected && (
              <Button
                onClick={fetchEvents}
                variant="outline"
                className="border-[#333] text-white hover:border-crimson"
                data-testid="refresh-events-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            )}
          </div>

          {/* Connection Status Card */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${calendarStatus.connected ? "bg-green-500/20" : "bg-gray-500/20"}`}>
                  {calendarStatus.connected ? (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  ) : (
                    <XCircle className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold">
                    Google Calendar {calendarStatus.connected ? "Connected" : "Not Connected"}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {calendarStatus.connected
                      ? "Your calendar is synced. Schedule interviews directly."
                      : "Connect your Google Calendar to schedule interviews."}
                  </p>
                </div>
              </div>
              <div>
                {calendarStatus.connected ? (
                  <Button
                    onClick={handleDisconnect}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="disconnect-calendar-btn"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="bg-crimson hover:bg-crimson-hover text-white"
                    data-testid="connect-calendar-btn"
                  >
                    {connecting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Connect Google Calendar
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Scheduled Interviews */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-crimson" />
                  Scheduled Interviews
                </h2>
              </div>

              {scheduledInterviews.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No interviews scheduled</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Schedule interviews from job applicant pages
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {scheduledInterviews.map((interview) => (
                    <div
                      key={interview.schedule_id}
                      className="p-4 bg-obsidian border border-[#333] hover:border-crimson/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-white">{interview.job_title}</h4>
                          <p className="text-gray-400 text-sm">{interview.candidate_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(interview.scheduled_time)}
                            </span>
                            <span>{interview.duration_minutes} min</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {interview.meet_link && (
                            <a
                              href={interview.meet_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-green-400 hover:bg-green-500/10 rounded transition-colors"
                            >
                              <Video className="w-4 h-4" />
                            </a>
                          )}
                          <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                            interview.status === "scheduled" ? "bg-blue-500/20 text-blue-400" :
                            interview.status === "completed" ? "bg-green-500/20 text-green-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {interview.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Calendar Events */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-crimson" />
                  Upcoming Events
                </h2>
                <span className="text-sm text-gray-500">Next 14 days</span>
              </div>

              {!calendarStatus.connected ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Connect your calendar to see events</p>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className="p-4 bg-obsidian border border-[#333] hover:border-crimson/30 transition-colors group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{event.title}</h4>
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(event.start)}
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1 truncate">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {event.link && (
                            <a
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-white transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => handleDeleteEvent(event.id)}
                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Help Section */}
          <motion.div
            className="mt-8 bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-heading text-lg font-bold mb-4">How to Schedule Interviews</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-crimson font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">Connect Calendar</h4>
                  <p className="text-gray-400 text-sm">Link your Google Calendar to sync events</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-crimson font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">View Applicants</h4>
                  <p className="text-gray-400 text-sm">Go to a job posting and view applicants</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-crimson font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">Schedule Interview</h4>
                  <p className="text-gray-400 text-sm">Click schedule to create a calendar event with Google Meet</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default CalendarIntegration;
